import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DateTimePicker } from "@/components/ui/DateTimePicker";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { FileUploadTrigger } from "@/components/native/FileUploadTrigger";
import {
  Calendar,
  Users,
  ArrowRight,
  Image,
  X,
  Upload,
  Percent,
  DollarSign,
  Tag,
} from "lucide-react";
import { useCampaignManagement } from "@/hooks/useCampaignManagement";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { adminLabels } from "@/i18n/labels/adminLabels";
import { commonButtonLabels } from "@/i18n/labels/commonLabels";
import { getTypeColor, getTypeLabel } from "@/utils/listingUtils";
import {
  SUPPORTED_IMAGE_TYPES,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
} from "@/config/constants";

interface CreateCampaignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreateCampaignDialog = ({
  open,
  onOpenChange,
}: CreateCampaignDialogProps) => {
  const [step, setStep] = useState<"type" | "details">("type");
  const [campaignType, setCampaignType] = useState<
    "auction" | "direct-purchase" | null
  >(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [thumbnailImage, setThumbnailImage] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string>("");
  const [labels, setLabels] = useState<
    { labelType: "reduced_percentage" | "special"; text: string }[]
  >([]);
  const [targetUserType, setTargetUserType] = useState<
    "all" | "dealers-only" | "individual-and-business-only"
  >("all");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { createCampaignAsync, isCreating } = useCampaignManagement();
  const { language } = useLanguage();
  const L = (adminLabels(language) as any)?.leadboxListings ?? {};

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);

  const resetForm = () => {
    setStep("type");
    setCampaignType(null);
    setName("");
    setDescription("");
    setStartDate(undefined);
    setEndDate(undefined);
    setThumbnailImage(null);
    setThumbnailPreview("");
    setLabels([]);
    setTargetUserType("all");
  };

  const handleTypeSelect = (type: "auction" | "direct-purchase") => {
    setCampaignType(type);
    setStep("details");
  };

  const handleBack = () => {
    resetForm();
  };

  const handleSubmit = async () => {
    if (!campaignType || !name || !description || !startDate || !endDate) {
      toast.error(L.allFieldsRequired);
      return;
    }

    if (!thumbnailImage) {
      toast.error(L.campaignImageRequired);
      return;
    }

    if (startDate >= endDate) {
      toast.error(L.endDateAfterStart);
      return;
    }

    // Validate file type and size using constants
    if (!SUPPORTED_IMAGE_TYPES.includes(thumbnailImage.type)) {
      const allowedExtensions = SUPPORTED_IMAGE_TYPES.map((t) => {
        const ext = t.split("/")[1].toUpperCase();
        return ext === "JPEG" || ext === "JPG" ? "JPEG" : ext;
      }).filter((v, i, a) => a.indexOf(v) === i); // Remove duplicates
      toast.error(
        L.invalidFileFormat?.replace("{types}", allowedExtensions.join(", ")),
      );
      return;
    }

    if (thumbnailImage.size > MAX_FILE_SIZE_BYTES) {
      toast.error(L.fileTooLarge?.replace("{mb}", String(MAX_FILE_SIZE_MB)));
      return;
    }

    // Duplicate name check is now handled in CampaignService.createCampaign

    // Filter out empty labels
    const filteredLabels = labels.filter((label) => label.text.trim() !== "");

    setIsSubmitting(true);

    try {
      const campaignData = {
        name,
        description,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        type: campaignType,
        targetUserType,
        thumbnailImage: thumbnailImage!,
        labels: filteredLabels,
      };

      await createCampaignAsync(campaignData);
      toast.success(L.campaignCreated);

      // Reset form
      resetForm();
      onOpenChange(false);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : L.campaignCreateError;
      toast.error(errorMessage);
      // Don't close dialog on error, allow user to fix and retry
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        toast.error(
          L.invalidFileFormat?.replace("{types}", "JPEG, PNG, GIF, WebP"),
        );
        event.target.value = ""; // Reset file input
        return;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(L.fileTooLarge?.replace("{mb}", String(MAX_FILE_SIZE_MB)));
        event.target.value = ""; // Reset file input
        return;
      }

      setThumbnailImage(file);
      // Create preview URL for display
      const reader = new FileReader();
      reader.onload = (e) => {
        setThumbnailPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setThumbnailImage(null);
    setThumbnailPreview("");
    toast.success(L.imageRemoved);
  };

  const predefinedLabels = [
    {
      key: "reduced_percentage",
      label: L.discountPercent ?? "Rabatt %",
      icon: Percent,
      color: "bg-red-100 text-red-800",
    },
    {
      key: "special",
      label: L.special ?? "Special",
      icon: Tag,
      color: "bg-blue-100 text-blue-800",
    },
  ];

  const handleAddLabel = () => {
    if (labels.length < 2) {
      setLabels([...labels, { labelType: "reduced_percentage", text: "" }]);
    }
  };

  const handleRemoveLabel = (index: number) => {
    if (labels.length > 0) {
      setLabels(labels.filter((_, i) => i !== index));
    }
  };

  const handleLabelChange = (
    index: number,
    field: "labelType" | "text",
    value: string,
  ) => {
    if (field === "text" && value.length > 10) return;
    const newLabels = [...labels];
    newLabels[index] = { ...newLabels[index], [field]: value };
    setLabels(newLabels);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[100%] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {step === "type" ? L.selectCampaignType : L.createCampaign}
          </DialogTitle>
          <DialogDescription>
            {step === "type" ? L.selectCampaignTypeDesc : L.fillCampaignInfo}
          </DialogDescription>
        </DialogHeader>

        {step === "type" ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <button
                onClick={() => handleTypeSelect("auction")}
                className="p-4 border rounded-lg hover:bg-accent/20 transition-colors text-left"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <Badge className={getTypeColor("auction")}>
                      {getTypeLabel("auction")}
                    </Badge>
                    <h3 className="font-semibold mt-2">{L.auction}</h3>
                    <p className="text-sm text-primary/60 mt-1">
                      {L.auctionDesc}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-primary/60" />
                </div>
              </button>

              <button
                onClick={() => handleTypeSelect("direct-purchase")}
                className="p-4 border rounded-lg hover:bg-accent/20 transition-colors text-left"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <Badge className={getTypeColor("direct-purchase")}>
                      {getTypeLabel("direct-purchase")}
                    </Badge>
                    <h3 className="font-semibold mt-2">{L.directPurchase}</h3>
                    <p className="text-sm text-primary/60 mt-1">
                      {L.directPurchaseDesc}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-primary/60" />
                </div>
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{L.campaignName}</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={L.campaignNamePlaceholder}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{L.description}</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={L.descriptionPlaceholder}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>{L.recipients}</Label>
              <p className="text-xs text-muted-foreground mb-1">
                {L.selectRecipients}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button
                  variant="outline"
                  onClick={() => setTargetUserType("all")}
                  className={`flex items-center justify-center gap-2 p-4 rounded-lg border-2 transition-all ${
                    targetUserType === "all"
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  <span>{L.all}</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setTargetUserType("dealers-only")}
                  className={`flex items-center justify-center gap-2 p-4 rounded-lg border-2 transition-all ${
                    targetUserType === "dealers-only"
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  <span>{L.dealers}</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={() =>
                    setTargetUserType("individual-and-business-only")
                  }
                  className={`p-4 rounded-lg border-2 transition-all text-center ${
                    targetUserType === "individual-and-business-only"
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  <span className="break-words whitespace-normal leading-tight inline-block">
                    {L.privateAndBusiness}
                  </span>
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">{L.startDate}</Label>
                <DateTimePicker
                  value={startDate ? startDate.toISOString() : ""}
                  onChange={(value) =>
                    setStartDate(value ? new Date(value) : undefined)
                  }
                  label=""
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="endDate">{L.endDate}</Label>
                <DateTimePicker
                  value={endDate ? endDate.toISOString() : ""}
                  onChange={(value) =>
                    setEndDate(value ? new Date(value) : undefined)
                  }
                  label=""
                  className="w-full"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 p-3 rounded-lg bg-card">
              <Badge className={getTypeColor(campaignType!)}>
                {getTypeLabel(campaignType!)}
              </Badge>
              <span className="text-sm text-primary/60">
                {L.selectedCampaignType}
              </span>
            </div>

            {/* Thumbnail Image */}
            <div className="space-y-2">
              <Label>{L.campaignImage}</Label>
              <div className="flex items-center gap-2">
                <FileUploadTrigger
                  accept="image/*"
                  onFilesSelected={(files) => {
                    if (!files[0]) return;
                    const dt = new DataTransfer();
                    dt.items.add(files[0]);
                    handleFileUpload({
                      target: { files: dt.files },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }}
                >
                  {(open) => (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={open}
                      className="flex items-center gap-2"
                    >
                      <Upload className="h-4 w-4" />
                      {L.uploadImage}
                    </Button>
                  )}
                </FileUploadTrigger>
              </div>
              {thumbnailPreview && (
                <div className="mt-2 relative inline-block">
                  <img
                    src={thumbnailPreview}
                    alt="Thumbnail preview"
                    className="w-32 h-20 object-cover rounded-lg border"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                    onClick={handleRemoveImage}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>

            {/* Labels */}
            <div className="space-y-2">
              <Label>{L.labelsOptionalMax2}</Label>

              {labels.map((label, index) => (
                <div key={index} className="space-y-2 p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <select
                      value={label.labelType}
                      onChange={(e) =>
                        handleLabelChange(index, "labelType", e.target.value)
                      }
                      className="flex-1 p-2 border rounded-md text-sm bg-card"
                    >
                      {predefinedLabels.map(
                        ({ key, label: labelName, icon: Icon }) => (
                          <option key={key} value={key}>
                            {labelName}
                          </option>
                        ),
                      )}
                    </select>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveLabel(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <Input
                    value={label.text}
                    onChange={(e) =>
                      handleLabelChange(index, "text", e.target.value)
                    }
                    placeholder={L.enterTextMax10}
                    maxLength={10}
                  />
                </div>
              ))}

              {labels.length < 2 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddLabel}
                  className="mx-4"
                >
                  <Tag className="h-4 w-4" />
                  {L.addLabel}
                </Button>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          {step === "details" && (
            <Button variant="outline" onClick={handleBack}>
              {commonButtonLabels(language)?.back ?? "Tillbaka"}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => {
              resetForm();
              onOpenChange(false);
            }}
          >
            {commonButtonLabels(language)?.cancel ?? "Avbryt"}
          </Button>
          {step === "details" && (
            <Button
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                isCreating ||
                !name ||
                !description ||
                !startDate ||
                !endDate ||
                !thumbnailImage
              }
            >
              {isSubmitting || isCreating ? L.creating : L.createCampaignButton}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
